import { Button, Checkbox, Group, ScrollArea, Stack, Text, Textarea } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useOverrideHooks } from '@override';
import { useState } from 'react';
import { z } from 'zod';

import { TemplateType } from '@e-origin/shared';

import { searchReleasedBatchesByNames } from '@hooks/batch';

interface NewReportRequestProps {
  onClose: () => void;
}

const requestReportSchema = z.object({
  batchNames: z.string().min(1, { message: 'Batch names are required' }),
  selectedBatches: z.array(z.string()).min(1, { message: 'Batch selection is required' }),
});

export const NewReportRequest: React.FC<NewReportRequestProps> = ({ onClose }) => {
  const OverrideHooks = useOverrideHooks();
  const [batchesFound, setBatchesFound] = useState([]);
  const generateBatchReportMutation = OverrideHooks.useGenerateBatchReport();

  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      batchNames: '',
      selectedBatches: [],
    },
    validate: zodResolver(requestReportSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);
    try {
      values.selectedBatches.forEach((batchId) => {
        generateBatchReportMutation.mutate({ batchId, setRequestedBy: true });
      });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to request reports');
    }
  };

  const handleRetrieveBatches = async () => {
    setBatchesFound([]);
    form.setFieldValue('selectedBatches', []);
    const results = await searchReleasedBatchesByNames(form.values.batchNames);

    setBatchesFound(
      results.map((result) => ({
        label: `${result.name} (${result.templateType === TemplateType.LOW_VALUE_H7 ? 'Low Value' : 'High Value'}) ${result.archived ? ' (Archived)' : ''}`,
        value: result.id,
        archived: result.archived,
      })),
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="lg" fw={500}>
            Batch names list
          </Text>
          <Button onClick={handleRetrieveBatches} disabled={form.values.batchNames.length === 0} size="sm">
            Retrieve Batches
          </Button>
        </Group>

        <Textarea {...form.getInputProps('batchNames')} minRows={7} autosize maxRows={7} />
        {batchesFound?.length > 0 && (
          <ScrollArea h={300} offsetScrollbars="y">
            <Stack gap="xs">
              <Checkbox
                label="All"
                key="all"
                checked={form.values.selectedBatches.length === batchesFound.length}
                indeterminate={form.values.selectedBatches.length !== batchesFound.length && form.values.selectedBatches.length > 0}
                onChange={() =>
                  form.setFieldValue('selectedBatches', form.values.selectedBatches.length === batchesFound.length ? [] : batchesFound.map((b) => b.value))
                }
              />
              <Checkbox.Group {...form.getInputProps('selectedBatches')}>
                {batchesFound.map((batch) => (
                  <Checkbox mt="xs" label={batch.label} value={batch.value} key={batch.value} />
                ))}
              </Checkbox.Group>
            </Stack>
          </ScrollArea>
        )}
        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} variant="transparent">
            Cancel
          </Button>
          <Button type="submit" disabled={!form.isValid()}>
            Request
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
